/**
 * Concept Graph Visualizer — force-directed graph visualization.
 *
 * Reuses the same architecture as SchemaGraphPage:
 * - Web Worker for force simulation (runs offline, posts back positions)
 * - Canvas rendering with viewport culling
 * - Spatial hit grid for O(1) hover/click
 * - Pan/zoom transform {x, y, k}
 * - Node dragging
 */
import React, { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { ConceptNode, ConceptEdge } from '../../services/docProcessingApi';

// ── Node type → color mapping ───────────────────────────────────────────

const TYPE_COLORS: Record<string, string> = {
  process:    '#6366f1',
  document:   '#10b981',
  equipment:  '#f59e0b',
  unit:       '#8b5cf6',
  transport:  '#06b6d4',
  operation:  '#f97316',
  charge:     '#ef4444',
  compliance: '#14b8a6',
  pricing:    '#ec4899',
  party:      '#84cc16',
  service:    '#0ea5e9',
  schedule:   '#a855f7',
  deadline:   '#e11d48',
  policy:     '#eab308',
  system:     '#64748b',
  status:     '#78716c',
  entity:     '#2dd4bf',
  concept:    '#818cf8',
  cargo_type: '#fb923c',
};

function getColor(nodeType: string): string {
  return TYPE_COLORS[nodeType] || '#94a3b8';
}

// ── Force simulation types ──────────────────────────────────────────────

interface SimNode {
  id: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  name: string;
  nodeType: string;
  module: string;
  color: string;
  radius: number;
  nameLen: number;
  pinned?: boolean;
  labelDir?: 'top' | 'bottom' | 'left' | 'right';
}

interface SimEdge {
  source: string;
  target: string;
  relation: string;
}

// ── Web Worker for force simulation ─────────────────────────────────────

const WORKER_CODE = `
self.onmessage = function(evt) {
  var data = evt.data;
  var nodes = data.nodes;
  var edges = data.edges;
  var width = data.width;
  var height = data.height;
  var iterations = data.iterations || 200;

  var nodeMap = {};
  for (var i = 0; i < nodes.length; i++) nodeMap[nodes[i].id] = nodes[i];

  // Collision radius based on name length
  for (var i = 0; i < nodes.length; i++) {
    var n = nodes[i];
    var labelHalfW = Math.min(n.nameLen || 6, 18) * 4.5;
    n.cr = Math.max(n.radius + 14, labelHalfW);
  }

  // Initialize positions in a circle
  var cx = width / 2;
  var cy = height / 2;
  var initRadius = Math.min(width, height) * 0.42;
  for (var i = 0; i < nodes.length; i++) {
    var angle = (2 * Math.PI * i) / nodes.length;
    nodes[i].x = cx + initRadius * Math.cos(angle) + (Math.random() - 0.5) * 40;
    nodes[i].y = cy + initRadius * Math.sin(angle) + (Math.random() - 0.5) * 40;
    nodes[i].vx = 0;
    nodes[i].vy = 0;
  }

  var alpha0 = 1.0;
  var alphaDecay = 1 - Math.pow(0.001, 1 / iterations);
  var cellSize = 180;

  for (var iter = 0; iter < iterations; iter++) {
    var alpha = alpha0 * Math.pow(1 - alphaDecay, iter);
    if (alpha < 0.001) break;

    // Spatial grid for O(n) repulsion
    var grid = {};
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      var gx = Math.floor(n.x / cellSize);
      var gy = Math.floor(n.y / cellSize);
      var key = gx + ',' + gy;
      if (!grid[key]) grid[key] = [];
      grid[key].push(i);
    }

    // Repulsion (charge force)
    for (var i = 0; i < nodes.length; i++) {
      var a = nodes[i];
      var gx = Math.floor(a.x / cellSize);
      var gy = Math.floor(a.y / cellSize);
      for (var dx = -1; dx <= 1; dx++) {
        for (var dy = -1; dy <= 1; dy++) {
          var key = (gx + dx) + ',' + (gy + dy);
          var cell = grid[key];
          if (!cell) continue;
          for (var ci = 0; ci < cell.length; ci++) {
            var j = cell[ci];
            if (j <= i) continue;
            var b = nodes[j];
            var ddx = b.x - a.x;
            var ddy = b.y - a.y;
            var dist = Math.sqrt(ddx * ddx + ddy * ddy) || 1;
            var strength = -1200 * alpha / (dist * dist);
            var fx = ddx / dist * strength;
            var fy = ddy / dist * strength;
            a.vx -= fx;
            a.vy -= fy;
            b.vx += fx;
            b.vy += fy;
          }
        }
      }
    }

    // Collision force
    for (var i = 0; i < nodes.length; i++) {
      var a = nodes[i];
      var gx = Math.floor(a.x / cellSize);
      var gy = Math.floor(a.y / cellSize);
      for (var dx = -1; dx <= 1; dx++) {
        for (var dy = -1; dy <= 1; dy++) {
          var key = (gx + dx) + ',' + (gy + dy);
          var cell = grid[key];
          if (!cell) continue;
          for (var ci = 0; ci < cell.length; ci++) {
            var j = cell[ci];
            if (j <= i) continue;
            var b = nodes[j];
            var ddx = b.x - a.x;
            var ddy = b.y - a.y;
            var dist = Math.sqrt(ddx * ddx + ddy * ddy) || 1;
            var minDist = (a.cr + b.cr) * 1.3;
            if (dist < minDist) {
              var overlap = (minDist - dist) * 0.5;
              var ox = (ddx / dist) * overlap;
              var oy = (ddy / dist) * overlap;
              a.vx -= ox;
              a.vy -= oy;
              b.vx += ox;
              b.vy += oy;
            }
          }
        }
      }
    }

    // Attraction (link force)
    for (var ei = 0; ei < edges.length; ei++) {
      var edge = edges[ei];
      var a = nodeMap[edge.source];
      var b = nodeMap[edge.target];
      if (!a || !b) continue;
      var ddx = b.x - a.x;
      var ddy = b.y - a.y;
      var dist = Math.sqrt(ddx * ddx + ddy * ddy) || 1;
      var targetDist = 200;
      var strength = (dist - targetDist) * 0.025 * alpha;
      var fx = ddx / dist * strength;
      var fy = ddy / dist * strength;
      a.vx += fx;
      a.vy += fy;
      b.vx -= fx;
      b.vy -= fy;
    }

    // Center force
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].vx += (cx - nodes[i].x) * 0.005 * alpha;
      nodes[i].vy += (cy - nodes[i].y) * 0.005 * alpha;
    }

    // Apply velocity with damping
    var maxV = 0;
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].vx *= 0.6;
      nodes[i].vy *= 0.6;
      nodes[i].x += nodes[i].vx;
      nodes[i].y += nodes[i].vy;
      var v = Math.abs(nodes[i].vx) + Math.abs(nodes[i].vy);
      if (v > maxV) maxV = v;
    }
    if (maxV < 0.5) break;
  }

  var positions = [];
  for (var i = 0; i < nodes.length; i++) {
    positions.push({ id: nodes[i].id, x: nodes[i].x, y: nodes[i].y });
  }
  self.postMessage({ positions: positions });
};
`;

let workerBlobUrl: string | null = null;
function getWorkerUrl(): string {
  if (!workerBlobUrl) {
    workerBlobUrl = URL.createObjectURL(new Blob([WORKER_CODE], { type: 'application/javascript' }));
  }
  return workerBlobUrl;
}

// ── Canvas renderer ─────────────────────────────────────────────────────

function drawGraph(
  ctx: CanvasRenderingContext2D,
  nodes: SimNode[],
  edges: SimEdge[],
  nodeMap: Map<string, SimNode>,
  transform: { x: number; y: number; k: number },
  hoveredNode: SimNode | null,
  selectedNode: SimNode | null,
  width: number,
  height: number,
) {
  ctx.clearRect(0, 0, width, height);
  ctx.save();
  ctx.translate(transform.x, transform.y);
  ctx.scale(transform.k, transform.k);

  // Viewport bounds for culling
  const invK = 1 / transform.k;
  const vLeft = -transform.x * invK - 80;
  const vTop = -transform.y * invK - 80;
  const vRight = (width - transform.x) * invK + 80;
  const vBottom = (height - transform.y) * invK + 80;

  // Connected set for selected node
  const connectedIds = new Set<string>();
  if (selectedNode) {
    for (const e of edges) {
      if (e.source === selectedNode.id) connectedIds.add(e.target);
      if (e.target === selectedNode.id) connectedIds.add(e.source);
    }
  }

  // ── Draw edges ──
  // Non-highlighted batch
  ctx.beginPath();
  ctx.strokeStyle = '#94a3b8';
  ctx.lineWidth = 1.2;
  ctx.globalAlpha = selectedNode ? 0.15 : 0.6;
  const highlightedEdges: SimEdge[] = [];
  for (const edge of edges) {
    const src = nodeMap.get(edge.source);
    const tgt = nodeMap.get(edge.target);
    if (!src || !tgt) continue;
    if ((src.x < vLeft && tgt.x < vLeft) || (src.x > vRight && tgt.x > vRight) ||
        (src.y < vTop && tgt.y < vTop) || (src.y > vBottom && tgt.y > vBottom)) continue;
    if (selectedNode && (edge.source === selectedNode.id || edge.target === selectedNode.id)) {
      highlightedEdges.push(edge);
      continue;
    }
    ctx.moveTo(src.x, src.y);
    ctx.lineTo(tgt.x, tgt.y);
  }
  ctx.stroke();
  ctx.globalAlpha = 1;

  // Highlighted edges
  if (highlightedEdges.length > 0) {
    ctx.beginPath();
    ctx.strokeStyle = '#6366f1';
    ctx.lineWidth = 2.5;
    for (const edge of highlightedEdges) {
      const src = nodeMap.get(edge.source)!;
      const tgt = nodeMap.get(edge.target)!;
      ctx.moveTo(src.x, src.y);
      ctx.lineTo(tgt.x, tgt.y);
    }
    ctx.stroke();
  }

  // Edge arrows + labels (only when zoomed in enough)
  if (transform.k > 0.4) {
    for (const edge of edges) {
      const src = nodeMap.get(edge.source);
      const tgt = nodeMap.get(edge.target);
      if (!src || !tgt) continue;
      if ((src.x < vLeft && tgt.x < vLeft) || (src.x > vRight && tgt.x > vRight)) continue;

      const isHighlighted = selectedNode && (edge.source === selectedNode.id || edge.target === selectedNode.id);
      if (selectedNode && !isHighlighted) continue; // skip dimmed edges for labels

      // Arrow
      const angle = Math.atan2(tgt.y - src.y, tgt.x - src.x);
      const arrowX = tgt.x - (tgt.radius + 5) * Math.cos(angle);
      const arrowY = tgt.y - (tgt.radius + 5) * Math.sin(angle);
      ctx.beginPath();
      ctx.moveTo(arrowX, arrowY);
      ctx.lineTo(arrowX - 8 * Math.cos(angle - 0.35), arrowY - 8 * Math.sin(angle - 0.35));
      ctx.lineTo(arrowX - 8 * Math.cos(angle + 0.35), arrowY - 8 * Math.sin(angle + 0.35));
      ctx.closePath();
      ctx.fillStyle = isHighlighted ? '#6366f1' : '#9ca3af';
      ctx.fill();

      // Relation label
      if (transform.k > 0.55) {
        const midX = (src.x + tgt.x) / 2;
        const midY = (src.y + tgt.y) / 2;
        ctx.font = '9px Inter, system-ui, sans-serif';
        ctx.fillStyle = isHighlighted ? '#4338ca' : '#9ca3af';
        ctx.textAlign = 'center';
        ctx.fillText(edge.relation, midX, midY - 5);
      }
    }
  }

  // ── Draw nodes ──
  const showLabels = transform.k > 0.35;
  for (const node of nodes) {
    if (node.x < vLeft || node.x > vRight || node.y < vTop || node.y > vBottom) continue;

    const isSelected = selectedNode?.id === node.id;
    const isHovered = hoveredNode?.id === node.id;
    const isConnected = selectedNode && connectedIds.has(node.id);

    if (selectedNode && !isSelected && !isConnected) {
      ctx.globalAlpha = 0.12;
    }

    // Circle
    ctx.beginPath();
    ctx.arc(node.x, node.y, node.radius, 0, 2 * Math.PI);
    ctx.fillStyle = node.color;
    ctx.fill();

    if (isSelected || isHovered) {
      ctx.strokeStyle = '#111827';
      ctx.lineWidth = 3;
      ctx.stroke();
    } else {
      ctx.strokeStyle = 'rgba(255,255,255,0.8)';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

    // Label
    if (showLabels) {
      ctx.font = (isSelected || isHovered) ? 'bold 11px Inter, system-ui, sans-serif' : '10px Inter, system-ui, sans-serif';
      ctx.fillStyle = '#111827';
      const label = node.name.length > 18 ? node.name.substring(0, 16) + '\u2026' : node.name;
      const pad = node.radius + 4;
      const dir = node.labelDir || 'bottom';

      if (dir === 'left') { ctx.textAlign = 'right'; ctx.textBaseline = 'middle'; ctx.fillText(label, node.x - pad, node.y); }
      else if (dir === 'right') { ctx.textAlign = 'left'; ctx.textBaseline = 'middle'; ctx.fillText(label, node.x + pad, node.y); }
      else if (dir === 'top') { ctx.textAlign = 'center'; ctx.textBaseline = 'bottom'; ctx.fillText(label, node.x, node.y - pad); }
      else { ctx.textAlign = 'center'; ctx.textBaseline = 'top'; ctx.fillText(label, node.x, node.y + pad); }
    }

    ctx.globalAlpha = 1;
  }

  ctx.restore();
}

// ── Label direction precomputation (spatial grid — O(n)) ────────────────

function precomputeLabelDirs(nodes: SimNode[]) {
  const cellSize = 100;
  const grid = new Map<string, number[]>();
  for (let i = 0; i < nodes.length; i++) {
    const gx = Math.floor(nodes[i].x / cellSize);
    const gy = Math.floor(nodes[i].y / cellSize);
    const key = `${gx},${gy}`;
    const arr = grid.get(key);
    if (arr) arr.push(i); else grid.set(key, [i]);
  }
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    const gx = Math.floor(node.x / cellSize);
    const gy = Math.floor(node.y / cellSize);
    let avgDx = 0, avgDy = 0, count = 0;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        const cell = grid.get(`${gx + dx},${gy + dy}`);
        if (!cell) continue;
        for (const j of cell) {
          if (j === i) continue;
          const ox = nodes[j].x - node.x;
          const oy = nodes[j].y - node.y;
          const d2 = ox * ox + oy * oy;
          if (d2 < 15000) { avgDx += ox; avgDy += oy; count++; }
        }
      }
    }
    if (count === 0) { node.labelDir = 'bottom'; }
    else if (Math.abs(avgDx) > Math.abs(avgDy)) { node.labelDir = avgDx > 0 ? 'left' : 'right'; }
    else { node.labelDir = avgDy > 0 ? 'top' : 'bottom'; }
  }
}

// ── Spatial hit grid for O(1) hover ─────────────────────────────────────

let _hitGrid: Map<string, SimNode[]> = new Map();
const _hitCellSize = 60;

function buildHitGrid(nodes: SimNode[]) {
  _hitGrid = new Map();
  for (const n of nodes) {
    const r = n.radius + 4;
    const x0 = Math.floor((n.x - r) / _hitCellSize);
    const x1 = Math.floor((n.x + r) / _hitCellSize);
    const y0 = Math.floor((n.y - r) / _hitCellSize);
    const y1 = Math.floor((n.y + r) / _hitCellSize);
    for (let gx = x0; gx <= x1; gx++) {
      for (let gy = y0; gy <= y1; gy++) {
        const key = `${gx},${gy}`;
        const arr = _hitGrid.get(key);
        if (arr) arr.push(n); else _hitGrid.set(key, [n]);
      }
    }
  }
}

function hitTestGrid(x: number, y: number): SimNode | null {
  const gx = Math.floor(x / _hitCellSize);
  const gy = Math.floor(y / _hitCellSize);
  const cell = _hitGrid.get(`${gx},${gy}`);
  if (!cell) return null;
  for (let i = cell.length - 1; i >= 0; i--) {
    const n = cell[i];
    const dx = n.x - x;
    const dy = n.y - y;
    if (dx * dx + dy * dy < (n.radius + 4) * (n.radius + 4)) return n;
  }
  return null;
}

// ── Props ───────────────────────────────────────────────────────────────

interface Props {
  nodes: ConceptNode[];
  edges: ConceptEdge[];
}

export default function ConceptGraphVisualizer({ nodes, edges }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const simNodesRef = useRef<SimNode[]>([]);
  const simEdgesRef = useRef<SimEdge[]>([]);
  const nodeMapRef = useRef<Map<string, SimNode>>(new Map());
  const animFrameRef = useRef<number>(0);
  const workerRef = useRef<Worker | null>(null);

  const [layouting, setLayouting] = useState(true);
  const [selectedNode, setSelectedNode] = useState<SimNode | null>(null);
  const [hoveredNode, setHoveredNode] = useState<SimNode | null>(null);
  const [transform, setTransform] = useState({ x: 0, y: 0, k: 1 });
  const transformRef = useRef(transform);
  transformRef.current = transform;

  const [interactionMode, setInteractionMode] = useState<'select' | 'pan'>('select');

  const isPanning = useRef(false);
  const panStart = useRef({ x: 0, y: 0 });
  const draggedNodeRef = useRef<SimNode | null>(null);
  const dragStartPos = useRef({ x: 0, y: 0 });
  const dragMoved = useRef(false);

  // ── Build sim data and run layout ──
  useEffect(() => {
    const w = containerRef.current?.clientWidth || 900;
    const h = 600;

    const simNodes: SimNode[] = nodes.map((n) => ({
      id: n.id,
      x: 0, y: 0, vx: 0, vy: 0,
      name: n.name,
      nameLen: n.name.length,
      nodeType: n.node_type,
      module: n.module,
      color: getColor(n.node_type),
      radius: Math.max(16, Math.min(28, 12 + n.name.length * 0.7)),
    }));

    const simEdges: SimEdge[] = edges.map(e => ({
      source: e.from,
      target: e.to,
      relation: e.relation,
    }));

    simNodesRef.current = simNodes;
    simEdgesRef.current = simEdges;
    const map = new Map<string, SimNode>();
    for (const n of simNodes) map.set(n.id, n);
    nodeMapRef.current = map;

    // Run force layout in Web Worker
    setLayouting(true);
    const worker = new Worker(getWorkerUrl());
    workerRef.current = worker;
    worker.onmessage = (evt) => {
      const positions: Array<{ id: string; x: number; y: number }> = evt.data.positions;
      for (const pos of positions) {
        const node = nodeMapRef.current.get(pos.id);
        if (node) { node.x = pos.x; node.y = pos.y; }
      }
      precomputeLabelDirs(simNodesRef.current);
      buildHitGrid(simNodesRef.current);
      setLayouting(false);
      setTransform({ x: 0, y: 0, k: 1 });
    };
    worker.postMessage({
      nodes: simNodes.map(n => ({ id: n.id, radius: n.radius, nameLen: n.nameLen })),
      edges: simEdges.map(e => ({ source: e.source, target: e.target })),
      width: w,
      height: h,
      iterations: 250,
    });

    return () => { worker.terminate(); };
  }, [nodes, edges]);

  // ── Render (on-demand, not continuous loop) ──
  const render = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);

    drawGraph(ctx, simNodesRef.current, simEdgesRef.current, nodeMapRef.current,
      transformRef.current, hoveredNode, selectedNode, w, h);
  }, [hoveredNode, selectedNode]);

  const scheduleRedraw = useCallback(() => {
    cancelAnimationFrame(animFrameRef.current);
    animFrameRef.current = requestAnimationFrame(render);
  }, [render]);

  useEffect(() => { scheduleRedraw(); }, [scheduleRedraw]);
  useEffect(() => { scheduleRedraw(); }, [transform, scheduleRedraw]);

  // Resize observer
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const ro = new ResizeObserver(() => scheduleRedraw());
    ro.observe(container);
    return () => ro.disconnect();
  }, [scheduleRedraw]);

  // ── Hit test ──
  const hitTest = useCallback((clientX: number, clientY: number): SimNode | null => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const t = transformRef.current;
    const x = (clientX - rect.left - t.x) / t.k;
    const y = (clientY - rect.top - t.y) / t.k;
    return hitTestGrid(x, y);
  }, []);

  // ── Mouse handlers (same pattern as SchemaGraphPage) ──
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    const node = hitTest(e.clientX, e.clientY);
    if (node) {
      draggedNodeRef.current = node;
      dragStartPos.current = { x: e.clientX, y: e.clientY };
      dragMoved.current = false;
    } else if (interactionMode === 'pan') {
      isPanning.current = true;
      const t = transformRef.current;
      panStart.current = { x: e.clientX - t.x, y: e.clientY - t.y };
    } else {
      setSelectedNode(null);
    }
  }, [hitTest, interactionMode]);

  const lastHoverTime = useRef(0);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (draggedNodeRef.current) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const t = transformRef.current;
      draggedNodeRef.current.x = (e.clientX - rect.left - t.x) / t.k;
      draggedNodeRef.current.y = (e.clientY - rect.top - t.y) / t.k;
      const totalMove = Math.abs(e.clientX - dragStartPos.current.x) + Math.abs(e.clientY - dragStartPos.current.y);
      if (totalMove > 4) dragMoved.current = true;
      scheduleRedraw();
      if (canvas) canvas.style.cursor = 'move';
    } else if (isPanning.current) {
      transformRef.current = {
        ...transformRef.current,
        x: e.clientX - panStart.current.x,
        y: e.clientY - panStart.current.y,
      };
      scheduleRedraw();
      if (canvasRef.current) canvasRef.current.style.cursor = 'grabbing';
    } else {
      const now = performance.now();
      if (now - lastHoverTime.current < 32) return;
      lastHoverTime.current = now;
      const node = hitTest(e.clientX, e.clientY);
      if (node !== hoveredNode) setHoveredNode(node);
      if (canvasRef.current) {
        canvasRef.current.style.cursor = node ? 'pointer' : (interactionMode === 'pan' ? 'grab' : 'default');
      }
    }
  }, [hitTest, scheduleRedraw, hoveredNode, interactionMode]);

  const handleMouseUp = useCallback(() => {
    if (draggedNodeRef.current) {
      if (!dragMoved.current) {
        setSelectedNode(draggedNodeRef.current);
      }
      draggedNodeRef.current = null;
    }
    if (isPanning.current) {
      setTransform({ ...transformRef.current });
    }
    isPanning.current = false;
    if (canvasRef.current) {
      canvasRef.current.style.cursor = interactionMode === 'pan' ? 'grab' : 'default';
    }
  }, [interactionMode]);

  // Wheel zoom — native listener with { passive: false }
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const rect = canvas.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      const zoom = e.deltaY < 0 ? 1.1 : 0.9;
      setTransform((t) => {
        const newK = Math.max(0.1, Math.min(5, t.k * zoom));
        return { k: newK, x: mx - (mx - t.x) * (newK / t.k), y: my - (my - t.y) * (newK / t.k) };
      });
    };
    canvas.addEventListener('wheel', handler, { passive: false });
    return () => canvas.removeEventListener('wheel', handler);
  }, []);

  // ── Fit to view ──
  const fitToView = useCallback(() => {
    const nodes = simNodesRef.current;
    if (nodes.length === 0) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const n of nodes) {
      if (n.x - n.radius < minX) minX = n.x - n.radius;
      if (n.y - n.radius < minY) minY = n.y - n.radius;
      if (n.x + n.radius > maxX) maxX = n.x + n.radius;
      if (n.y + n.radius > maxY) maxY = n.y + n.radius;
    }
    const pad = 60;
    const w = canvas.clientWidth - pad * 2;
    const h = canvas.clientHeight - pad * 2;
    const graphW = maxX - minX || 1;
    const graphH = maxY - minY || 1;
    const k = Math.min(w / graphW, h / graphH, 2);
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    setTransform({ k, x: canvas.clientWidth / 2 - cx * k, y: canvas.clientHeight / 2 - cy * k });
  }, []);

  // ── Zoom by button ──
  const zoomBy = useCallback((factor: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const cx = canvas.clientWidth / 2;
    const cy = canvas.clientHeight / 2;
    setTransform((t) => {
      const newK = Math.max(0.1, Math.min(5, t.k * factor));
      return { k: newK, x: cx - (cx - t.x) * (newK / t.k), y: cy - (cy - t.y) * (newK / t.k) };
    });
  }, []);

  // ── Legend ──
  const usedTypes = useMemo(
    () => Array.from(new Set(nodes.map(n => n.node_type))).sort(),
    [nodes],
  );

  // ── Node detail panel ──
  const connectedNodes = useMemo(() => {
    if (!selectedNode) return [];
    const ids = new Set<string>();
    for (const e of simEdgesRef.current) {
      if (e.source === selectedNode.id) ids.add(e.target);
      if (e.target === selectedNode.id) ids.add(e.source);
    }
    return simNodesRef.current.filter(n => ids.has(n.id));
  }, [selectedNode]);

  const selectedEdges = useMemo(() => {
    if (!selectedNode) return [];
    return simEdgesRef.current.filter(e => e.source === selectedNode.id || e.target === selectedNode.id);
  }, [selectedNode]);

  const totalNodes = simNodesRef.current.length;
  const totalEdges = simEdgesRef.current.length;

  return (
    <div ref={containerRef} className="relative bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" style={{ height: 600 }}>
      {layouting && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-20">
          <div className="text-sm text-gray-500">Computing layout...</div>
        </div>
      )}

      {/* ── Toolbar (same as SchemaGraphPage) ── */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, zIndex: 15,
        display: 'flex', alignItems: 'center', padding: '8px 16px', gap: 8,
        background: 'rgba(255,255,255,0.95)', borderBottom: '1px solid #e5e7eb',
        backdropFilter: 'blur(4px)',
      }}>
        {/* Select / Pan toggle */}
        <div style={{
          display: 'inline-flex', borderRadius: 6, border: '1px solid #d1d5db',
          overflow: 'hidden', fontSize: 12,
        }}>
          <button
            onClick={() => setInteractionMode('select')}
            title="Select mode: click nodes to inspect"
            style={{
              padding: '4px 10px', border: 'none', cursor: 'pointer',
              background: interactionMode === 'select' ? '#4f46e5' : 'white',
              color: interactionMode === 'select' ? 'white' : '#374151',
              fontWeight: 500,
            }}
          >
            Select
          </button>
          <button
            onClick={() => setInteractionMode('pan')}
            title="Pan mode: drag canvas to move around"
            style={{
              padding: '4px 10px', border: 'none', cursor: 'pointer',
              borderLeft: '1px solid #d1d5db',
              background: interactionMode === 'pan' ? '#4f46e5' : 'white',
              color: interactionMode === 'pan' ? 'white' : '#374151',
              fontWeight: 500,
            }}
          >
            Pan
          </button>
        </div>

        {/* Zoom buttons */}
        <button
          onClick={() => zoomBy(1.3)}
          title="Zoom in"
          style={{
            background: 'none', border: '1px solid #d1d5db', borderRadius: 6,
            padding: '4px 8px', fontSize: 14, cursor: 'pointer', color: '#374151', fontWeight: 600,
          }}
        >
          +
        </button>
        <button
          onClick={() => zoomBy(1 / 1.3)}
          title="Zoom out"
          style={{
            background: 'none', border: '1px solid #d1d5db', borderRadius: 6,
            padding: '4px 8px', fontSize: 14, cursor: 'pointer', color: '#374151', fontWeight: 600,
          }}
        >
          −
        </button>
        <button
          onClick={fitToView}
          title="Fit graph to viewport"
          style={{
            background: 'none', border: '1px solid #d1d5db', borderRadius: 6,
            padding: '4px 10px', fontSize: 12, cursor: 'pointer', color: '#374151',
          }}
        >
          Fit
        </button>

        {/* Node/edge count */}
        <div style={{ fontSize: 11, color: '#9ca3af' }}>
          {totalNodes} nodes &middot; {totalEdges} edges
        </div>
      </div>

      {/* Legend (top-right, below toolbar) */}
      <div className="absolute top-12 right-3 bg-white/95 backdrop-blur-sm rounded-lg border border-gray-200 px-3 py-2 z-10 max-h-72 overflow-y-auto">
        <div className="text-xs font-semibold text-gray-500 mb-1.5">Node Types</div>
        <div className="space-y-0.5">
          {usedTypes.map(t => (
            <div key={t} className="flex items-center gap-2 text-xs text-gray-600">
              <span className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0" style={{ backgroundColor: getColor(t) }} />
              {t}
            </div>
          ))}
        </div>
      </div>

      {/* Selected node detail (bottom-left) */}
      {selectedNode && (
        <div className="absolute bottom-3 left-3 bg-white rounded-xl border border-gray-200 shadow-lg px-4 py-3 z-10 max-w-xs">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <div className="text-xs font-semibold uppercase" style={{ color: selectedNode.color }}>{selectedNode.nodeType}</div>
              <div className="text-base font-bold text-gray-900">{selectedNode.name}</div>
              <div className="text-xs text-gray-500">{selectedNode.module}</div>
            </div>
            <button onClick={() => setSelectedNode(null)} className="text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
          </div>
          {selectedEdges.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Relationships ({selectedEdges.length})</div>
              <div className="space-y-0.5">
                {selectedEdges.map((e, i) => {
                  const fromName = nodeMapRef.current.get(e.source)?.name || e.source;
                  const toName = nodeMapRef.current.get(e.target)?.name || e.target;
                  return (
                    <div key={i} className="text-xs text-gray-600">
                      <span className="font-medium">{fromName}</span>
                      <span className="mx-1 text-purple-500">{e.relation}</span>
                      <span className="font-medium">{toName}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {connectedNodes.length > 0 && (
            <div className="mt-2">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Connected ({connectedNodes.length})</div>
              <div className="flex flex-wrap gap-1">
                {connectedNodes.map(cn => (
                  <span key={cn.id} className="text-xs px-2 py-0.5 rounded-full" style={{ background: cn.color + '20', color: cn.color, border: `1px solid ${cn.color}40` }}>
                    {cn.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <canvas
        ref={canvasRef}
        style={{ width: '100%', height: '100%' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      />
    </div>
  );
}
